'use client'

import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useCallback } from 'react'

import { useHolidayContext } from '@/src/contexts'
import {
    buildHolidayCollectionPayload,
    buildHolidayPackagePayload,
    getHolidayCampaignParams,
    resolveHolidayPageType,
    HOLIDAY_ACTION_TYPE,
    HOLIDAY_EVENT_NAME,
    type HolidayActionName,
    type HolidayEventPayload,
    type HolidayUIActionPayload
} from '@/src/utils/analytics/holidays/holidayEvents'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { logError } from '@/src/utils/logError'

import useClevertapEvent from '../index'

/**
 * Holidays CleverTap events. The page context — which route, which collection, which package —
 * comes from `HolidayContext`, so a call site only names its action and what the action itself
 * adds. The index route has no provider and reports as the index page with no document context.
 */
const useClevertapForHolidays = () => {
    const { triggerEvent } = useClevertapEvent()
    const { packageData, collectionData, domainCountry } = useHolidayContext()
    const locale = useLocale()
    const pathname = usePathname()

    const buildHolidayPagePayload = useCallback((): HolidayEventPayload => {
        const pageType = resolveHolidayPageType(Boolean(packageData), Boolean(collectionData))
        const pagePath = pathname || '/'

        const base: HolidayEventPayload = {
            page_type: pageType,
            page_name: pageType,
            current_page: pageType,
            page_path: pagePath,
            landing_page: pagePath,
            page_language: locale,
            page_currency: getUserSelectedCurrencyCode()?.toLowerCase() || 'NA',
            page_referrer: typeof window === 'undefined' ? 'no_page_referrer' : document.referrer || 'no_page_referrer',
            market: domainCountry || 'NA',
            product_category: 'holidays',
            ...getHolidayCampaignParams()
        }

        if (packageData) return { ...base, ...buildHolidayPackagePayload(packageData) }
        if (collectionData) return { ...base, ...buildHolidayCollectionPayload(collectionData) }
        return base
    }, [packageData, collectionData, locale, pathname, domainCountry])

    const triggerHolidayPageLoadEvent = useCallback(
        (actionName: HolidayActionName, payload: HolidayEventPayload = {}) => {
            try {
                triggerEvent(HOLIDAY_EVENT_NAME.PAGE_LOAD, {
                    ...buildHolidayPagePayload(),
                    action_name: actionName,
                    action_type: HOLIDAY_ACTION_TYPE.PAGE_LOAD,
                    ...payload
                })
            } catch (e) {
                logError('triggerHolidayPageLoadEvent', e)
            }
        },
        [triggerEvent, buildHolidayPagePayload]
    )

    const triggerHolidayUIActionEvent = useCallback(
        (actionName: HolidayActionName, payload: HolidayUIActionPayload) => {
            try {
                triggerEvent(HOLIDAY_EVENT_NAME.UI_ACTION, {
                    ...buildHolidayPagePayload(),
                    action_name: actionName,
                    ...payload
                })
            } catch (e) {
                logError('triggerHolidayUIActionEvent', e)
            }
        },
        [triggerEvent, buildHolidayPagePayload]
    )

    return { triggerHolidayPageLoadEvent, triggerHolidayUIActionEvent }
}

export { useClevertapForHolidays }
export default useClevertapForHolidays
